import { useRef, useState, type KeyboardEvent } from 'react';

export interface TagsInputProps {
  label?: string;
  defaultTags?: string[];
  placeholder?: string;
  onChange?: (tags: string[]) => void;
  /**
   * Optional validator. A typed value is only committed to a tag when this returns true;
   * invalid input is kept in the field (and flagged) so the user can correct it.
   */
  validate?: (value: string) => boolean;
  /**
   * Characters that commit the current value to a tag as soon as they are typed (in addition
   * to Enter). Defaults to none. Pass [',', ' '] to auto-tag on comma or space.
   */
  separators?: string[];
  /** Native input type (e.g. "email"). */
  inputType?: string;
  /** Help text shown under the field. */
  hint?: string;
}

/** Tag editor — type and press Enter (or a separator) to add chips; Backspace removes the last. */
export function TagsInput({
  label,
  defaultTags = [],
  placeholder = 'Add tag…',
  onChange,
  validate,
  separators = [],
  inputType = 'text',
  hint,
}: TagsInputProps) {
  const [tags, setTags] = useState<string[]>(defaultTags);
  const [val, setVal] = useState('');
  const [invalid, setInvalid] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const update = (next: string[]) => {
    setTags(next);
    onChange?.(next);
  };
  /** Commit the current input as a tag; returns true if it was accepted. */
  const add = (): boolean => {
    const v = val.trim();
    if (!v) return false;
    if (validate && !validate(v)) {
      setInvalid(true);
      return false;
    }
    if (!tags.includes(v)) update([...tags, v]);
    setVal('');
    setInvalid(false);
    return true;
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      add();
    } else if (separators.includes(e.key)) {
      // Auto-tag as soon as a separator is typed (only when the value is valid).
      if (val.trim()) {
        e.preventDefault();
        add();
      }
    } else if (e.key === 'Backspace' && !val && tags.length) {
      update(tags.slice(0, -1));
    }
  };

  return (
    <div className="mb-3">
      {label && <label className="form-label">{label}</label>}
      <div
        className={`form-control d-flex flex-wrap align-items-center gap-1 ${invalid ? 'is-invalid' : ''}`.trim()}
        onClick={() => inputRef.current?.focus()}
        role="presentation"
      >
        {tags.map((t) => (
          <span key={t} className="badge bg-primary d-inline-flex align-items-center">
            {t}
            <button
              type="button"
              className="btn-close btn-close-white ms-1"
              style={{ fontSize: '0.5rem' }}
              aria-label={`Remove ${t}`}
              onClick={(e) => {
                e.stopPropagation();
                update(tags.filter((x) => x !== t));
              }}
            />
          </span>
        ))}
        <input
          ref={inputRef}
          type={inputType}
          className="border-0 flex-grow-1 p-0"
          style={{ outline: 'none', minWidth: 80 }}
          value={val}
          onChange={(e) => {
            setVal(e.target.value);
            if (invalid) setInvalid(false);
          }}
          onKeyDown={onKey}
          onBlur={() => {
            // Commit a valid value on blur so a finished entry becomes a tag automatically.
            if (val.trim()) add();
          }}
          placeholder={tags.length ? '' : placeholder}
        />
      </div>
      {hint && <div className={invalid ? 'invalid-feedback d-block' : 'form-text'}>{hint}</div>}
    </div>
  );
}
